import { Outlet, Link, useLocation } from 'react-router-dom'
import {
  Home,
  Building2,
  ClipboardList,
  User,
  LogOut,
  Menu,
  X,
  Settings,
  ScrollText,
  Target,
  Database,
  Shield,
  ChevronDown,
  PanelLeftClose,
  PanelLeft,
  Sparkles
} from 'lucide-react'
import { useState, useRef, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { useAuthentication } from '@/features/auth/AuthenticationContext'
import { useAuthorization } from '@/features/authorization/AuthorizationContext'
import type { LucideIcon } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

interface NavItem {
  name: string
  href: string
  icon: LucideIcon
}

interface GatedNavItem extends NavItem {
  requiredActions: string[]
  mode?: 'any' | 'all'
}

const baseNavigation: GatedNavItem[] = [
  { name: 'Home', href: '/', icon: Home, requiredActions: [] },
  { name: 'Organizations', href: '/organizations', icon: Building2, requiredActions: ['view_organization'] },
  { name: 'Assessments', href: '/assessments', icon: ClipboardList, requiredActions: ['view_assessment'] },
  { name: 'Attachments', href: '/assessments/attachments', icon: ScrollText, requiredActions: ['view_assessment'] },
]

const settingsNavItems: GatedNavItem[] = [
  { name: 'Assessment Engine', href: '/settings/assessment-engine', icon: Database, requiredActions: ['view_domains', 'view_questions', 'view_maturity_levels', 'view_scoring_labels'], mode: 'any' },
  { name: 'Recommendation Engine', href: '/settings/recommendations', icon: Target, requiredActions: ['manage_industries', 'manage_initiatives'], mode: 'any' },
  { name: 'Settings', href: '/settings', icon: Settings, requiredActions: ['view_settings', 'update_settings'], mode: 'any' },
  { name: 'Security', href: '/settings/security', icon: Shield, requiredActions: ['view_all_users', 'view_actions'], mode: 'any' },
]

const auditTrailNav: GatedNavItem = {
  name: 'Audit Trail',
  href: '/audit-trail',
  icon: ScrollText,
  requiredActions: ['view_audit_trail'],
}

const aiNav: GatedNavItem = {
  name: 'AI Assistant',
  href: '/ai',
  icon: Sparkles,
  requiredActions: ['use_ai_chat'],
}

function NavItemComponent({ item, pathname, collapsed, onNavigate }: { item: NavItem; pathname: string; collapsed?: boolean; onNavigate?: () => void }) {
  const isActive = pathname === item.href

  const link = (
    <Link
      to={item.href}
      className={`group flex items-center ${collapsed ? 'justify-center p-2' : 'px-2 py-2'} text-sm font-medium rounded-md transition-colors ${isActive
        ? 'bg-blue-100 text-blue-700'
        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
        }`}
      onClick={onNavigate}
    >
      <item.icon className={`h-5 w-5 shrink-0 ${collapsed ? '' : 'mr-3'}`} />
      {!collapsed && <span className="truncate">{item.name}</span>}
    </Link>
  )

  if (collapsed) {
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right" sideOffset={8}>
          {item.name}
        </TooltipContent>
      </Tooltip>
    )
  }

  return link
}

/** Top-right profile dropdown with user email, profile link, and sign-out */
function ProfileMenu({ user, onLogout }: { user: any; onLogout: () => void }) {
  const [open, setOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as HTMLElement)) {
        setOpen(false)
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-x-2 rounded-full p-1 hover:bg-gray-100 transition-colors"
      >
        <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center">
          <span className="text-sm font-medium text-white">
            {user?.name?.charAt(0).toUpperCase()}
          </span>
        </div>
        <span className="hidden lg:block text-sm font-medium text-gray-700">
          {user?.name}
        </span>
        <ChevronDown className="hidden lg:block h-4 w-4 text-gray-400" />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-64 origin-top-right rounded-lg bg-white shadow-lg ring-1 ring-black/5 z-50">
          {/* User info header */}
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-medium text-gray-900">{user?.name}</p>
            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
          </div>

          {/* Menu items */}
          <div className="py-1">
            <Link
              to="/profile"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <User className="h-4 w-4 text-gray-400" />
              Profile
            </Link>
          </div>

          {/* Sign out */}
          <div className="border-t border-gray-100 py-1">
            <button
              onClick={() => { setOpen(false); onLogout() }}
              className="flex w-full items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const location = useLocation()
  const { user, logout } = useAuthentication()
  const { hasAnyAction } = useAuthorization()

  const filterGatedItems = (items: GatedNavItem[]) =>
    items.filter(item =>
      item.requiredActions.length === 0
        ? true
        : item.mode === 'any'
          ? hasAnyAction(...item.requiredActions)
          : hasAnyAction(...item.requiredActions)
    )

  const navigation: NavItem[] = [
    ...filterGatedItems(baseNavigation),
    ...filterGatedItems(settingsNavItems),
    ...(hasAnyAction(...auditTrailNav.requiredActions) ? [auditTrailNav] : []),
    ...(hasAnyAction(...aiNav.requiredActions) ? [aiNav] : []),
  ]

  const handleLogout = async () => {
    await logout()
  }

  const sidebarWidth = sidebarCollapsed ? 'lg:w-16' : 'lg:w-64'
  const contentPadding = sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-gray-50">
        {/* Mobile sidebar */}
        <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
          <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-white">
            <div className="flex h-16 items-center justify-between px-4">
              <img
                src="/vertowave.png"
                alt="Verto Wave"
                className="h-7 w-auto"
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="h-6 w-6" />
              </Button>
            </div>
            <nav className="flex-1 space-y-1 px-2 py-4">
              {navigation.map((item) => (
                <NavItemComponent
                  key={item.name}
                  item={item}
                  pathname={location.pathname}
                  onNavigate={() => setSidebarOpen(false)}
                />
              ))}
            </nav>
          </div>
        </div>

        {/* Desktop sidebar */}
        <div className={`hidden lg:fixed lg:inset-y-0 lg:flex ${sidebarWidth} lg:flex-col transition-all duration-300 ease-in-out`}>
          <div className="flex flex-col flex-grow bg-white border-r border-gray-200">
            <div className={`flex h-16 items-center ${sidebarCollapsed ? 'justify-center px-2' : 'justify-between px-4'}`}>
              {!sidebarCollapsed && (
                <img
                  src="/vertowave.png"
                  alt="Verto Wave"
                  className="h-8 w-auto"
                />
              )}
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
              >
                {sidebarCollapsed ? (
                  <PanelLeft className="h-5 w-5" />
                ) : (
                  <PanelLeftClose className="h-5 w-5" />
                )}
              </button>
            </div>
            <nav className={`flex-1 space-y-1 px-2 py-4`}>
              {navigation.map((item) => (
                <NavItemComponent
                  key={item.name}
                  item={item}
                  pathname={location.pathname}
                  collapsed={sidebarCollapsed}
                />
              ))}
            </nav>
          </div>
        </div>

        {/* Main content */}
        <div className={`${contentPadding} transition-all duration-300 ease-in-out`}>
          {/* Top bar */}
          <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-6 w-6" />
            </Button>

            <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
              <div className="flex flex-1 items-center">
                <h1 className="text-lg font-semibold text-gray-900">IT Maturity Assessment</h1>
              </div>
              <div className="flex items-center gap-x-4 lg:gap-x-6">
                <div className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-200" />
                <ProfileMenu user={user} onLogout={handleLogout} />
              </div>
            </div>
          </div>

          {/* Page content */}
          <main className="py-6">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </TooltipProvider>
  )
}
